'use client';

import { Button, Card, InlineToast, Input, Logo } from '@clickt/brand';
import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Check, Mail } from 'lucide-react';
import { useState } from 'react';
import { authClient } from '../lib/auth-client.ts';

const HIGHLIGHTS = [
  '3 tracked videos free forever',
  'Per-video revenue, even cross-device',
  '2-minute install. No code required.',
];

export function SignupPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [signedUp, setSignedUp] = useState(false);

  const mutation = useMutation({
    mutationFn: async (vars: { email: string; password: string; displayName: string }) => {
      const { data, error } = await authClient.signUp.email({
        email: vars.email,
        password: vars.password,
        name: vars.displayName,
        display_name: vars.displayName,
      } as Parameters<typeof authClient.signUp.email>[0]);
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      setErrorMessage(null);
      setSignedUp(true);
    },
    onError: (err: { message?: string }) => {
      setErrorMessage(err.message ?? 'Signup failed.');
    },
  });

  // Hold the loading state through the success swap so the button never reverts
  // to "Start tracking" between the mutation resolving and the success card
  // rendering — same coherent-flow guard as the login button.
  const isSubmitting = mutation.isPending || mutation.isSuccess;

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Dark left panel */}
      <aside className="surface-premium-dark relative overflow-hidden lg:flex-1 px-8 lg:px-12 py-8 lg:py-16 text-white">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              'radial-gradient(60% 50% at 75% 20%, rgba(47,91,255,0.35) 0%, rgba(47,91,255,0) 70%)',
          }}
        />
        <div className="relative max-w-md mx-auto lg:mx-0 space-y-8">
          <a
            href="https://clickt.link"
            className="inline-flex items-center rounded-sm focus-visible:outline-none focus-visible:shadow-focus-ring"
            aria-label="clickt home"
          >
            <Logo variant="lockup-light" className="h-8 w-auto" />
          </a>
          <div className="space-y-4">
            <p className="text-caption uppercase tracking-[0.08em] font-medium text-electric">
              FOR CREATORS WHO SELL
            </p>
            <h1 className="font-display font-bold tracking-[-0.02em] leading-[1.05] text-[clamp(2rem,4.5vw,3.2rem)]">
              Stop guessing which video sold the course.
            </h1>
            <p className="text-body-lg text-white/70 leading-relaxed">
              clickt traces every sale back to the video that drove it. Even when the buyer watches
              on phone and pays on desktop a week later.
            </p>
          </div>
          <ul className="space-y-3 list-none p-0">
            {HIGHLIGHTS.map((h) => (
              <li key={h} className="flex items-center gap-3 text-[15px] text-white/85">
                <span
                  aria-hidden
                  className="grid h-5 w-5 flex-none place-items-center rounded-pill bg-electric"
                >
                  <Check className="h-3 w-3 text-white" strokeWidth={3} />
                </span>
                {h}
              </li>
            ))}
          </ul>
        </div>
      </aside>

      {/* Form right panel */}
      <main className="lg:flex-1 px-6 lg:px-10 py-10 lg:py-16 flex flex-col bg-white">
        <p className="text-[14px] text-cool-gray self-end">
          Already have an account?{' '}
          <Link
            to="/login"
            className="text-midnight font-medium hover:text-electric transition-colors duration-fast"
          >
            Sign in
          </Link>
        </p>
        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md mx-auto space-y-6">
            {signedUp ? (
              <Card padding="lg">
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="grid h-12 w-12 place-items-center rounded-pill bg-electric/10">
                    <Mail className="h-6 w-6 text-electric" aria-hidden />
                  </div>
                  <h2 className="font-display text-display-3 font-bold text-midnight leading-tight tracking-[-0.01em]">
                    Check your email.
                  </h2>
                  <p className="text-body-md text-cool-gray leading-relaxed">
                    We sent a verification link to <span className="font-medium">{email}</span>.
                    Click it to finish creating your account.
                  </p>
                </div>
              </Card>
            ) : (
              <>
                <div className="space-y-2">
                  <h2 className="font-display text-display-3 font-bold text-midnight leading-tight tracking-[-0.01em]">
                    Create your account.
                  </h2>
                  <p className="text-body-md text-cool-gray leading-relaxed">
                    Free for your first 3 videos. No credit card.
                  </p>
                </div>
                <form
                  className="space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    mutation.mutate({ email, password, displayName });
                  }}
                >
                  {errorMessage ? (
                    <InlineToast
                      tone="danger"
                      title="Couldn't sign you up."
                      description={errorMessage}
                    />
                  ) : null}
                  <Input
                    label="Name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    required
                    autoComplete="name"
                    placeholder="Your name"
                  />
                  <Input
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="you@yourdomain.com"
                  />
                  <Input
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    placeholder="At least 8 characters"
                    hint="8+ characters. Avoid reusing passwords."
                  />
                  <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? 'Creating account…' : 'Start tracking'}
                    {!isSubmitting && <ArrowRight className="h-4 w-4" />}
                  </Button>
                  <p className="text-[12px] text-cool-gray leading-relaxed">
                    By signing up you agree to our{' '}
                    <a
                      href="https://clickt.link/terms"
                      className="text-midnight underline decoration-cool-gray hover:decoration-midnight"
                    >
                      Terms
                    </a>
                    ,{' '}
                    <a
                      href="https://clickt.link/privacy"
                      className="text-midnight underline decoration-cool-gray hover:decoration-midnight"
                    >
                      Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a
                      href="https://clickt.link/refund"
                      className="text-midnight underline decoration-cool-gray hover:decoration-midnight"
                    >
                      Refund Policy
                    </a>
                    .
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
